import { Text, useColorMode, useTheme } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { z } from 'zod';

import { TypographyPattern } from '~/packages/common-frontend/theme/patterns';
import { FontWeights } from '~/packages/common-frontend/theme/tokens';
import { ColorScheme } from '~/packages/common-frontend/theme/types';

// Types
export const TYPOGRAPHY_VARIANTS = [
  'page-title',
  'section-title',
  'section-subtitle',
  'base',
  'base-muted',
  'detail',
  'detail-muted',
] as const;
export const TypographyVariantStruct = z
  .enum(TYPOGRAPHY_VARIANTS)
  .default('base');
export type TypographyVariant = z.infer<typeof TypographyVariantStruct>;

// Exports
export type TypographyProps = {
  children?: React.ReactNode;
  colorScheme?: ColorScheme;
  inline?: boolean;
  fontWeight?: FontWeights;
  trim?: boolean;
  variant?: TypographyVariant;
  TextProps?: React.ComponentProps<typeof Text>;
};
/**
 * Variants:
 * - `page-title`:       The primary page header. There should only be one of these on any given route
 * - `section-title`:    On pages with section, use this. Ex. page sections, modal and drawer titles, etc.
 * - `section-subtitle`: On pages where sections have subsections, use this. Ex. chart title when next to other charts in a section
 * - `base`:             (default) Font style used for general page content. Ex. paragraphs, lists, buttons, inputs, etc.
 * - `base-muted`:       Same as "base", but slightly lighter. Use when you want to de-emphasize text.
 * - `detail`:           Smaller font style for secondary page text content. Ex. tooltips, input error messages, etc.
 * - `detail-muted`:     Same as "detail", but slightly lighter. Use when you want to de-emphasize text.
 *
 * Pass `trim=true` to get rid of default margins
 *
 * Pass `inline=true` to render text as `display: inline`. Also swaps out `<p>` tags for `<span>`s for non-headings
 *
 * NOTE: `colorScheme` has no effect on headings. They are always rendered as gray.950/white
 */

export const Typography = React.forwardRef<
  HTMLParagraphElement,
  TypographyProps
>((props: TypographyProps, ref) => {
  const {
    children,
    colorScheme = 'gray',
    inline = false,
    fontWeight,
    trim = false,
    variant = 'base',
    TextProps,
  } = props;

  const theme = useTheme();
  const { colorMode } = useColorMode();

  const variantProps: React.ComponentProps<typeof Text> = useMemo(() => {
    const styleProps = { colorMode, colorScheme, theme };
    switch (variant) {
      case 'page-title':
      case 'section-title':
      case 'section-subtitle': {
        const varProps = TypographyPattern.variants[variant](styleProps);
        return {
          as: (() => {
            if (variant === 'page-title') return 'h1';
            if (variant === 'section-title') return 'h2';
            if (variant === 'section-subtitle') return 'h3';
            throw Error(`Unexpected typography [variant=${variant}]`);
          })(),
          sx: {
            ...varProps,
            display: inline ? 'inline' : 'block',
            fontWeight: fontWeight ?? varProps.fontWeight,
            marginBottom: trim ? 0 : '1.5rem',
          },
        };
      }

      case 'base':
      case 'base-muted':
      case 'detail':
      case 'detail-muted': {
        const varProps = TypographyPattern.variants[variant](styleProps);
        return {
          as: inline ? 'span' : 'p',
          sx: {
            ...varProps,
            fontWeight: fontWeight ?? varProps.fontWeight,
            display: inline ? 'inline' : 'block',
          },
        };
      }
      default: {
        throw Error(`Invalid <Typography /> variant[${variant}]`);
      }
    }
  }, [colorMode, colorScheme, fontWeight, inline, theme, trim, variant]);

  return (
    <Text
      ref={ref}
      as={variantProps.as}
      {...TextProps}
      sx={{
        ...variantProps.sx,
        ...TextProps?.sx,
      }}
    >
      {children}
    </Text>
  );
});
Typography.displayName = 'Typography';
